import { useState, useEffect, useRef, useCallback } from 'react';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const FALLBACK_TOKEN = process.env.REACT_APP_PARTYKIT_VIEWER_TOKEN || '';
const MAX_WAIT_MS = 3000;
const POLL_INTERVAL = 200;
const MAX_ATTEMPTS = 2;

/**
 * Hook that invisibly verifies the user via Cloudflare Turnstile,
 * then exchanges the Turnstile token for a signed session token.
 * After MAX_ATTEMPTS failures, falls back to the static viewer token
 * so users aren't locked out.
 *
 * Returns: { token, ready, error, retry }
 */
export default function useTurnstileToken() {
  const [token, setToken] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);
  const attemptsRef = useRef(0);

  const attempt = useCallback(() => {
    setReady(false);
    setError(false);
    setToken(null);
    attemptsRef.current++;

    if (!SITE_KEY) {
      setToken(FALLBACK_TOKEN);
      setReady(true);
      return;
    }

    let waited = 0;
    const poll = setInterval(() => {
      waited += POLL_INTERVAL;

      if (typeof window.turnstile !== 'undefined') {
        clearInterval(poll);
        renderWidget();
        return;
      }

      if (waited >= MAX_WAIT_MS) {
        clearInterval(poll);
        handleFailure();
      }
    }, POLL_INTERVAL);

    if (typeof window.turnstile !== 'undefined') {
      clearInterval(poll);
      renderWidget();
    }

    function handleFailure() {
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        // Fall back to static token so users aren't locked out
        setToken(FALLBACK_TOKEN);
        setReady(true);
      } else {
        // Auto-retry immediately
        setTimeout(() => attempt(), 500);
      }
    }

    function renderWidget() {
      if (containerRef.current) {
        try { document.body.removeChild(containerRef.current); } catch {}
      }

      const div = document.createElement('div');
      div.style.display = 'none';
      document.body.appendChild(div);
      containerRef.current = div;

      const timeout = setTimeout(handleFailure, 5000);

      try {
        window.turnstile.render(div, {
          sitekey: SITE_KEY,
          callback: async (turnstileToken) => {
            clearTimeout(timeout);
            try {
              const res = await fetch('/api/events?action=turnstile-auth', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ turnstileToken }),
              });
              if (res.ok) {
                const data = await res.json();
                setToken(data.sessionToken);
                setReady(true);
              } else {
                handleFailure();
              }
            } catch {
              handleFailure();
            }
          },
          'error-callback': () => { clearTimeout(timeout); handleFailure(); },
          'expired-callback': () => { clearTimeout(timeout); handleFailure(); },
        });
      } catch {
        clearTimeout(timeout);
        handleFailure();
      }
    }
  }, []);

  useEffect(() => {
    // Delay init to let Cloudflare page challenge complete first
    const delay = setTimeout(() => attempt(), 2000);
    return () => {
      clearTimeout(delay);
      if (containerRef.current) {
        try { document.body.removeChild(containerRef.current); } catch {}
      }
    };
  }, [attempt]);

  return { token, ready, error, retry: attempt };
}
